import React, {useCallback, useRef, useState} from 'react'

import {useRafThrottle} from './useRafThrottle'

type useDragRangeProps = {
  onDragEnd: (range: {percentStart: number; percentEnd: number}) => void
  onMouseMove: (range: {percentStart: number; percentEnd: number}) => void
  disabled?: boolean
}

export function useDragRange({onDragEnd, onMouseMove, disabled}: useDragRangeProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dragStartX, setDragStartX] = useState<number | null>(null)
  const [dragEndX, setDragEndX] = useState<number | null>(null)
  const throttledMouseMove = useRafThrottle(onMouseMove)

  const handleMouseDown = useCallback(({clientX}: React.MouseEvent) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setDragStartX(clientX - rect.left)
      setDragEndX(null)
    }
  }, [])

  const handleMouseMove = useCallback(
    ({clientX}: React.MouseEvent) => {
      if (dragStartX !== null && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        setDragEndX(clientX - rect.left)

        if (dragEndX !== null) {
          const percentStart = dragStartX / rect.width
          const percentEnd = dragEndX / rect.width
          throttledMouseMove?.({
            percentStart,
            percentEnd
          })
        }
      }
    },
    [dragEndX, dragStartX, throttledMouseMove]
  )

  const handleMouseUp = useCallback(() => {
    if (
      dragStartX !== null &&
      dragEndX !== null &&
      containerRef.current &&
      dragStartX !== dragEndX
    ) {
      const {width} = containerRef.current.getBoundingClientRect()
      const [startX, endX] = [dragStartX, dragEndX].sort((a, b) => a - b)
      const percentStart = startX / width
      const percentEnd = endX / width

      onDragEnd({percentStart, percentEnd})
    }
    setDragStartX(null)
    setDragEndX(null)
  }, [dragStartX, dragEndX, onDragEnd])

  return {
    containerRef,
    dragStartX,
    dragEndX,
    handleMouseDown: disabled ? undefined : handleMouseDown,
    handleMouseMove: disabled ? undefined : handleMouseMove,
    handleMouseUp: disabled ? undefined : handleMouseUp
  }
}
